import { de, enUS } from 'date-fns/locale';
import { i18n } from '../i18n';

/**
 * Get the date-fns locale based on the current i18n language
 */
export function getDateLocale() {
  const currentLanguage = i18n.language;
  
  switch (currentLanguage) {
    case 'en':
      return enUS;
    case 'de':
    default:
      return de;
  }
}

/**
 * Format a date according to the current locale
 * @param date - Date to format
 * @param options - Intl.DateTimeFormatOptions (optional)
 */
export function formatDate(
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const currentLanguage = i18n.language;
  const locale = currentLanguage === 'en' ? 'en-US' : 'de-DE';
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  
  return dateObj.toLocaleDateString(locale, options || defaultOptions);
}

/**
 * Format a datetime according to the current locale
 */
export function formatDateTime(
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const currentLanguage = i18n.language;
  const locale = currentLanguage === 'en' ? 'en-US' : 'de-DE';
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  
  return dateObj.toLocaleString(locale, options || defaultOptions);
}

/**
 * Format a time according to the current locale
 */
export function formatTime(
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!date) return '-';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const currentLanguage = i18n.language;
  const locale = currentLanguage === 'en' ? 'en-US' : 'de-DE';
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  
  return dateObj.toLocaleTimeString(locale, options || defaultOptions);
}

/**
 * Format a Date as a local calendar day (YYYY-MM-DD).
 *
 * Use this instead of `toISOString().split('T')[0]`, which yields the *UTC* day and is
 * therefore off by one between midnight and the local UTC offset.
 */
export function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse a YYYY-MM-DD calendar day into a Date at local midnight.
 *
 * Counterpart to {@link isoDate}. Plain `new Date('2026-08-14')` parses as UTC midnight,
 * which mixes badly with the local getters/setters used for date arithmetic.
 */
export function parseIsoDate(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00`);
}

/**
 * Add days to a YYYY-MM-DD calendar day, staying in local time.
 */
export function addIsoDays(dateStr: string, days: number): string {
  const d = parseIsoDate(dateStr);
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

/**
 * Parse a UTC timestamp string from the API into a proper Date object.
 * Ensures the timestamp is treated as UTC even if the 'Z' suffix is missing.
 */
export function parseUtcTimestamp(timestamp: string | null | undefined): Date | null {
  if (!timestamp) return null;
  let utcString = timestamp;
  if (!/[Zz]$/.test(utcString) && !/[+-]\d{2}(:\d{2})?$/.test(utcString)) {
    utcString += 'Z';
  }
  const date = new Date(utcString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Format a UTC timestamp from the API to local time (HH:mm).
 */
export function formatUtcToLocalTime(timestamp: string | null | undefined): string {
  const date = parseUtcTimestamp(timestamp);
  if (!date) return '--:--';
  const currentLanguage = i18n.language;
  const locale = currentLanguage === 'en' ? 'en-US' : 'de-DE';
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Format a UTC timestamp from the API to a local time input value (HH:mm).
 */
export function utcToLocalTimeInputValue(timestamp: string | null | undefined): string {
  const date = parseUtcTimestamp(timestamp);
  if (!date) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/**
 * Convert a local time string (HH:mm) on a given date to a UTC ISO string.
 */
export function localTimeToUtcIso(dateStr: string, timeStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);
  const local = new Date(year, month - 1, day, hours, minutes, 0, 0);
  return local.toISOString();
}

/**
 * Clock-in window: whole hours, `startHour` inclusive, `endHour` exclusive. It gates only
 * clocking in — clock-out and breaks are always allowed so a late finish can still be recorded.
 * Work time counts from `workCountStartHour`. The tenant sets it in the time clock policy, a
 * time clock group can override it per season (FACF-149); `timeZoneId` null means the
 * browser's local time.
 */
export interface ClockInWindow {
  isEnabled: boolean;
  startHour: number;
  endHour: number;
  workCountStartHour: number;
  timeZoneId: string | null;
}

/** Backend default, used while the tenant's policy is loading or could not be loaded. */
export const DEFAULT_CLOCK_IN_WINDOW: ClockInWindow = {
  isEnabled: true,
  startHour: 5,
  endHour: 20,
  workCountStartHour: 6,
  timeZoneId: null,
};

/** Local calendar date (`YYYY-MM-DD`) of `now` in `timeZoneId`; null = browser time. */
export function isoDateIn(now: Date, timeZoneId: string | null): string {
  if (!timeZoneId) return isoDate(now);
  try {
    // why: en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timeZoneId,
    }).format(now);
  } catch {
    return isoDate(now);
  }
}

/** Local hour (0–23) of `now` in `timeZoneId`; null = browser time. */
export function hourIn(now: Date, timeZoneId: string | null): number {
  if (!timeZoneId) return now.getHours();
  try {
    const hour = new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric',
      hourCycle: 'h23',
      timeZone: timeZoneId,
    }).format(now);
    return Number(hour);
  } catch {
    // why: an unknown zone id must not lock everyone out — fall back to the browser's clock.
    return now.getHours();
  }
}

/**
 * Check if `now` is within the clock-in window. A disabled policy never blocks clocking in.
 */
export function isWithinClockInWindow(
  window: ClockInWindow = DEFAULT_CLOCK_IN_WINDOW,
  now: Date = new Date(),
): boolean {
  if (!window.isEnabled) return true;
  const hour = hourIn(now, window.timeZoneId);
  return hour >= window.startHour && hour < window.endHour;
}

/** Formats a whole hour as "HH:00" for hint texts. */
export function formatWindowHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}
